import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import showToast from '../../utils/toastHelper';
import ConfirmModal from '../../components/ConfirmModal';

const Elections = () => {
  const navigate = useNavigate();
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [electionToDelete, setElectionToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pricingPackages, setPricingPackages] = useState([]);
  const [organizationRequiresPayment, setOrganizationRequiresPayment] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'draft',
    accessType: 'closed',
    pricingPackageId: '',
  });

  useEffect(() => {
    fetchElections();
    fetchOrganizationInfo();
    fetchPricingPackages();
  }, []);

  const fetchElections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setElections(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch elections:', error);
      showToast('Failed to load elections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch current user profile to get organizationId
      const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = userResponse.data.data;
      
      
      if (!user.organizationId) {
        console.error('No organization ID found for user');
        return;
      }
      
      // Fetch organization details
      const orgResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organizations/${user.organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    
      setOrganizationRequiresPayment(orgResponse.data.data?.requiresPayment || false);

      // Check for active annual subscription
      if (orgResponse.data.data?.requiresPayment && user.organizationId) {
        try {
          const subResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/subscriptions/organization/${user.organizationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setActiveSubscription(subResponse.data.data);
        } catch (subError) {
          console.error('Failed to fetch subscription:', subError);
        }
      }
    } catch (error) {
      console.error('Failed to fetch organization info:', error);
    }
  };

  const fetchPricingPackages = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/pricing-packages`);
      setPricingPackages(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch pricing packages:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate dates
    const now = new Date();
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    
    if (endDate <= startDate) {
      showToast('End date must be after start date', 'error');
      return;
    }
    
    if (startDate < now) {
      showToast('Start date cannot be in the past', 'error');
      return;
    }
    
    // Validate package selection for paid organizations (skip if annual subscription active)
    if (organizationRequiresPayment && !activeSubscription && !formData.pricingPackageId) {
      showToast('Please select a pricing package', 'error');
      return;
    }
    
    // Validate election duration against package limit
    if (organizationRequiresPayment && !activeSubscription && formData.pricingPackageId && formData.startDate && formData.endDate) {
      const selectedPackage = pricingPackages.find(pkg => pkg.id === formData.pricingPackageId);
      if (selectedPackage) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        
        if (durationDays > selectedPackage.durationDays) {
          showToast(`Election duration (${durationDays} days) exceeds the package limit of ${selectedPackage.durationDays} days`, 'error');
          return;
        }
      }
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create the election first
      const electionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/elections`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const createdElection = electionResponse.data.data;
      
      // If organization requires payment and package selected (and no active subscription), initiate payment
      if (organizationRequiresPayment && !activeSubscription && formData.pricingPackageId) {
        // const selectedPackage = pricingPackages.find(pkg => pkg.id === formData.pricingPackageId);
        
        // Initiate payment for the pricing package
        const paymentResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/payments/package/initiate`,
          {
            electionId: createdElection.id,
            packageId: formData.pricingPackageId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (paymentResponse.data.success && paymentResponse.data.data.paymentLink) {
          // Save election ID for redirect after payment
          localStorage.setItem('pendingElectionId', createdElection.id);
          localStorage.setItem('pendingElectionPayment', 'true');
          
          // Show success message
          showToast('Election created! Redirecting to payment...', 'success');
          
          // Redirect to payment gateway
          setTimeout(() => {
            window.location.href = paymentResponse.data.data.paymentLink;
          }, 1500);
        }
      } else {
        // No payment required (free/unlimited organizations)
        showToast('Election created successfully', 'success');
        setShowModal(false);
        setFormData({ name: '', description: '', startDate: '', endDate: '', status: 'draft', accessType: 'open', pricingPackageId: '' });
        fetchElections();
      }
    } catch (error) {
      console.error('Failed to create election:', error);
      showToast(error.response?.data?.error || 'Failed to create election', 'error');
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (election) => {
    setElectionToDelete(election);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/elections/${electionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Election deleted successfully', 'success');
      setShowDeleteModal(false);
      setElectionToDelete(null);
      fetchElections();
    } catch (error) {
      console.error('Failed to delete election:', error);
      showToast('Failed to delete election', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = { draft: '#6b7280', active: '#10b981', completed: '#3b82f6', cancelled: '#ef4444' };
    return colors[status] || '#6b7280';
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1" style={{ fontWeight: '600' }}>Elections</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage your organization's elections</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn"
          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '0.5rem 1.25rem', fontWeight: '600', fontSize: '0.875rem', borderRadius: '8px' }}
        >
          <i className="bi bi-plus-circle me-2"></i>Create Election
        </button>
      </div>

      {/* Stats row */}
      <div className="row mb-4">
        {[
          { label: 'Total', value: elections.length, icon: 'bi-calendar-event', color: '#667eea' },
          { label: 'Active', value: elections.filter(e => e.status === 'active').length, icon: 'bi-lightning-charge', color: '#10b981' },
          { label: 'Draft', value: elections.filter(e => e.status === 'draft').length, icon: 'bi-pencil', color: '#f59e0b' },
          { label: 'Completed', value: elections.filter(e => e.status === 'completed').length, icon: 'bi-check-circle', color: '#3b82f6' },
        ].map((s, i) => (
          <div key={i} className="col-6 col-lg-3 mb-3">
            <div className="card h-100" style={{ border: 'none', borderLeft: `4px solid ${s.color}` }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                    <div className="h4 mb-0 mt-1" style={{ fontWeight: '700' }}>{s.value}</div>
                  </div>
                  <i className={s.icon} style={{ fontSize: '1.75rem', color: s.color, opacity: 0.25 }}></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Election list */}
      <div className="card" style={{ border: 'none' }}>
        <div className="card-body p-0">
          {elections.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-calendar-event" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No elections yet. Create your first election to get started.</p>
            </div>
          ) : (
            elections.map((election, idx) => (
              <div
                key={election.id}
                className="d-flex justify-content-between align-items-center px-4 py-3"
                onClick={() => navigate(`/admin/elections/${election.id}`)}
                style={{ cursor: 'pointer', borderBottom: idx < elections.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.15s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: `${getStatusColor(election.status)}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem', flexShrink: 0 }}>
                    <i className="bi bi-calendar-event" style={{ color: getStatusColor(election.status), fontSize: '1.1rem' }}></i>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#1e293b' }}>{election.name}</div>
                    {election.description && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                        {election.description}
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3" style={{ flexShrink: 0 }}>
                  <div className="d-none d-md-block text-muted" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    {formatDate(election.startDate)} — {formatDate(election.endDate)}
                  </div>
                  <span className="badge" style={{ backgroundColor: getStatusColor(election.status), textTransform: 'capitalize', fontSize: '0.7rem', fontWeight: '600' }}>
                    {election.status}
                  </span>
                  <div className="text-center" style={{ minWidth: '36px' }}>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#2c3e50' }}>{election.positions?.length || 0}</div>
                    <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>pos.</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(election); }}
                    style={{ color: '#d1d5db', background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer', fontSize: '1rem', transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                  <i className="bi bi-chevron-right" style={{ color: '#d1d5db', fontSize: '0.9rem' }}></i>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Election Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Election</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Start Date *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">End Date *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      required
                    />
                    {formData.startDate && formData.endDate && organizationRequiresPayment && formData.pricingPackageId && (() => {
                      const selectedPackage = pricingPackages.find(pkg => pkg.id === formData.pricingPackageId);
                      if (selectedPackage) {
                        const start = new Date(formData.startDate);
                        const end = new Date(formData.endDate);
                        const durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                        const isExceeded = durationDays > selectedPackage.durationDays;
                        
                        return (
                          <small className={`mt-1 d-block ${isExceeded ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>
                            {isExceeded ? (
                              <>
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                Duration: {durationDays} days (exceeds {selectedPackage.durationDays} day limit)
                              </>
                            ) : (
                              <>
                                <i className="bi bi-info-circle me-1"></i>
                                Duration: {durationDays} of {selectedPackage.durationDays} days allowed
                              </>
                            )}
                          </small>
                        );
                      }
                    })()}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Election Access</label>
                    <select
                      className="form-select"
                      value={formData.accessType}
                      onChange={(e) => setFormData({ ...formData, accessType: e.target.value })}
                    >
                      <option value="open">Open (Public - Anyone can vote)</option>
                      <option value="closed">Closed (Organization Members Only)</option>
                    </select>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {formData.accessType === 'open' 
                        ? 'Anyone who registers can participate in this election'
                        : 'Only users added to your organization can vote'}
                    </small>
                  </div>

                  {/* Pricing Package Selection for Paid Organizations */}
                  {organizationRequiresPayment && !activeSubscription && (
                    <>
                      <hr className="my-4" />
                      <h6 className="mb-3" style={{ fontWeight: '600', fontSize: '0.875rem', color: '#4b5563' }}>Pricing Package *</h6>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem' }}>Select a package for this election. The package determines voter limits, position limits, and available features.</p>
                      
                      <div className="mb-3">
                        <div className="row g-3">
                          {pricingPackages.map((pkg) => (
                            <div key={pkg.id} className="col-md-4">
                              <div
                                className={`card h-100 cursor-pointer ${
                                  formData.pricingPackageId === pkg.id ? 'border-primary shadow' : ''
                                }`}
                                style={{
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  borderWidth: formData.pricingPackageId === pkg.id ? '2px' : '1px',
                                }}
                                onClick={() => setFormData({ ...formData, pricingPackageId: pkg.id })}
                              >
                                <div className="card-body">
                                  <h6 className="card-title" style={{ fontWeight: '600' }}>{pkg.name}</h6>
                                  <div className="mb-2" style={{ fontSize: '1.25rem', fontWeight: '700', color: '#667eea' }}>
                                   {new Intl.NumberFormat('en-US', { style: 'currency', currency: pkg?.currency , minimumFractionDigits: 0 }).format(pkg?.price || 0)}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', lineHeight: '1.5' }}>
                                    <div>✓ {pkg.maxVoters || 'Unlimited'} voters</div>
                                    <div>✓ {pkg.maxPositions || 'Unlimited'} positions</div>
                                    <div>✓ {pkg.durationDays} days active</div>
                                    {pkg.multiVotingEnabled && <div>✓ Multi-voting</div>}
                                    {pkg.customBrandingEnabled && <div>✓ Custom branding</div>}
                                    {pkg.advancedAnalyticsEnabled && <div>✓ Advanced analytics</div>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {!formData.pricingPackageId && (
                          <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                            Please select a pricing package
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Active subscription banner */}
                  {organizationRequiresPayment && activeSubscription && (
                    <div className="alert mb-3" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontSize: '0.85rem' }}>
                      <i className="bi bi-check-circle-fill me-2"></i>
                      <strong>Annual Subscription Active</strong> — Your organization has an active subscription until {new Date(activeSubscription.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}. Elections can be created without per-election payment.
                    </div>
                  )}

                  {/* Only show voting config if no payment required OR package is selected OR subscription active, AND election is open */}
                  {formData.accessType === 'open' && (!organizationRequiresPayment || activeSubscription || (organizationRequiresPayment && formData.pricingPackageId)) && (() => {
                    const selectedPackage = organizationRequiresPayment 
                      ? pricingPackages.find(pkg => pkg.id === formData.pricingPackageId)
                      : null;
                    
                    const canEnableMultiVoting = !organizationRequiresPayment || selectedPackage?.multiVotingEnabled;

                    return (
                      <>
                        <hr className="my-4" />
                        <h6 className="mb-3" style={{ fontWeight: '600', fontSize: '0.875rem', color: '#4b5563' }}>Voting Configuration (Optional)</h6>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem' }}>Set default voting rules for all positions. Leave blank to configure per position.</p>
                        
                        {canEnableMultiVoting ? (
                          <>
                            <div className="mb-3">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="multiVote"
                                  checked={formData.multiVoteEnabled || false}
                                  onChange={(e) => setFormData({ ...formData, multiVoteEnabled: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="multiVote">
                                  Enable Multiple Voting
                                </label>
                              </div>
                            </div>

                            {formData.multiVoteEnabled && (
                              <>
                                <div className="row">
                                  <div className="col-md-6 mb-3">
                                    <label className="form-label">Vote Price</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      className="form-control"
                                      value={formData.votePrice || ''}
                                      onChange={(e) => setFormData({ ...formData, votePrice: e.target.value })}
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="col-md-6 mb-3">
                                    <label className="form-label">Currency</label>
                                    <select
                                      className="form-select"
                                      value={formData.currency || 'UGX'}
                                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                    >
                                      <option value="UGX">UGX (Ugandan Shilling)</option>
                                      <option value="USD">USD</option>
                                      <option value="GBP">GBP</option>
                                      <option value="EUR">EUR</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">Max Votes Per User</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={formData.maxVotesPerUser || ''}
                                    onChange={(e) => setFormData({ ...formData, maxVotesPerUser: e.target.value })}
                                    placeholder="Leave empty for unlimited"
                                  />
                                </div>
                                {!formData.allowGuestVoting && (
                                  <div className="mb-3">
                                    <div className="form-check">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="firstVoteFree"
                                      checked={formData.firstVoteFree === true}
                                        onChange={(e) => setFormData({ ...formData, firstVoteFree: e.target.checked })}
                                      />
                                      <label className="form-check-label" htmlFor="firstVoteFree">
                                        First Vote Free
                                      </label>
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      When enabled, the first vote is free and additional votes require payment. When disabled, ALL votes (including the first) require payment.
                                    </small>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        ) : organizationRequiresPayment && (
                          <div className="mb-3">
                            <div className="alert alert-warning" style={{ fontSize: '0.875rem', padding: '0.75rem' }}>
                              <i className="bi bi-lock me-2"></i>
                              Multiple voting is not available in your selected package. Upgrade to enable this feature.
                            </div>
                          </div>
                        )}

                        {/* Guest Voting - available regardless of multi-vote setting */}
                        {formData.accessType === 'open' && formData.multiVoteEnabled && formData.votePrice > 0 && (
                          <div className="mb-3">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="allowGuestVoting"
                                checked={formData.allowGuestVoting || false}
                                onChange={(e) => setFormData({ ...formData, allowGuestVoting: e.target.checked })}
                              />
                              <label className="form-check-label" htmlFor="allowGuestVoting">
                                <i className="bi bi-phone me-1"></i>
                                Allow Guest Voting (Phone Number)
                              </label>
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                              Allow non-registered users to vote using their phone number. Guests must pay for each vote. Only available for public elections with vote price.
                            </small>
                          </div>
                        )}

                        <div className="mb-3">
                          <label className="form-label">Results Visibility</label>
                          <select
                            className="form-select"
                            value={formData.resultsVisibility || ''}
                            onChange={(e) => setFormData({ ...formData, resultsVisibility: e.target.value })}
                          >
                            <option value="">Not set (configure per position)</option>
                            <option value="public">Public (anyone can view)</option>
                            <option value="voters_only">Voters Only</option>
                            <option value="hidden">Hidden (admins only)</option>
                            <option value="after_voting">After Voting Ends</option>
                          </select>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating...
                      </>
                    ) : (
                      'Create Election'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showDeleteModal}
        title="Delete Election"
        message={`Are you sure you want to delete "${electionToDelete?.name}"? This action cannot be undone and will remove all associated positions and candidates.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setElectionToDelete(null);
        }}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Elections;
